/**
 * ColumnsPanel — the tenant-wide list layout editor.
 *
 * The one thing to keep in mind reading this: **the layout is not a
 * preference.** It is one shared setting for the whole organisation, so every
 * decision here is about not losing another administrator's work:
 *
 *  · The draft is local until Save. Cancel, Escape, the backdrop and × all ask
 *    before discarding a dirty draft; a clean panel closes silently.
 *  · Save sends ONE version-checked PUT carrying the OTHER settings from the
 *    same loaded version, so saving columns can never rewrite the entity noun,
 *    the external-ID system or the default sort.
 *  · A 409 keeps the draft and offers a choice. It never re-submits with a
 *    fresher version — that is precisely how one administrator's save silently
 *    eats another's.
 *  · A configured field the catalog no longer offers is kept, marked, and must
 *    be removed by hand. Dropping it quietly would delete a column nobody
 *    decided to delete.
 *  · Reset only rewrites the DRAFT, and only to the platform default. The
 *    active industry preset is not stored in this resource, so guessing one
 *    would be inventing a layout.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Banner, Btn, Drawer, Icon, Input, T } from "@v0y4ge/jrni-ui";
import { toast } from "../../shell/toast";
import { useIsMobile } from "../../shell/useResponsive";
import { useT } from "../../lib/i18n";
import {
  getCustomerListFieldCatalog, getCustomersSettings, updateCustomersSettings, scopeProbe, useScopedRead,
  type CustomerListField, type CustomersSettings,
} from "./customers-data";
import {
  CUSTOMER_LIST_MAX_COLUMNS, PLATFORM_DEFAULT_COLUMNS, columnSource, columnsEqual, indexCatalog,
  isCustomerListColumnId, normalizeColumns,
} from "./customer-columns";
import { codeOf, isApiError, isDenied } from "./customer-types";
import { SourceBadge, useColumnLabel, useCustomerNoun, useIcu } from "./customers-shared";

type Group = "standard" | "sis" | "custom";
const GROUP_ORDER: Group[] = ["standard", "sis", "custom"];

interface FieldIssue { id: string | null; message: string }

export default function ColumnsPanel({ settings, onClose, onSaved }: {
  /** What the LIST is rendering — used only until the panel's own fresh read
   *  lands, so the panel never edits a version it did not just read. */
  settings: CustomersSettings;
  onClose: () => void;
  onSaved: (saved: CustomersSettings) => void;
}) {
  const t = useT();
  const icu = useIcu();
  const { nounPlural } = useCustomerNoun();
  const phone = useIsMobile(640);

  const settingsRead = useScopedRead("columns-panel-settings", getCustomersSettings);
  const catalogRead = useScopedRead("columns-panel-catalog", getCustomerListFieldCatalog);

  const base = settingsRead.data?.data ?? null;
  const fields = catalogRead.data?.data.fields ?? null;
  const byId = useMemo(() => indexCatalog(fields ?? []), [fields]);
  const columnLabel = useColumnLabel(byId);

  const [draft, setDraft] = useState<string[] | null>(null);
  const [search, setSearch] = useState("");
  const [collapsed, setCollapsed] = useState<Record<Group, boolean>>({ standard: false, sis: false, custom: false });
  const [saving, setSaving] = useState(false);
  const [issues, setIssues] = useState<FieldIssue[]>([]);
  const [banner, setBanner] = useState<{ tone: "warning" | "error"; text: string; conflict?: boolean } | null>(null);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const summaryRef = useRef<HTMLDivElement | null>(null);
  const rowRefs = useRef<Record<string, HTMLLIElement | null>>({});

  // The draft is seeded ONCE per loaded version. A later reload (conflict →
  // "Reload saved settings") deliberately re-seeds by clearing it first.
  useEffect(() => { if (base && draft === null) setDraft(normalizeColumns(base.columns)); }, [base, draft]);

  const visible = draft ?? normalizeColumns(settings.columns);
  const dirty = base ? !columnsEqual(visible, normalizeColumns(base.columns)) : false;
  const atLimit = visible.length >= CUSTOMER_LIST_MAX_COLUMNS;

  const catalogLoaded = !!fields;
  const retired = catalogLoaded ? visible.filter((id) => id !== "name" && !byId.has(id)) : [];
  const hasRetired = retired.length > 0;

  /* ── draft edits ───────────────────────────────────────────────────── */
  const setColumns = (next: string[]) => { setDraft(normalizeColumns(next)); setIssues([]); setBanner((b) => (b?.conflict ? b : null)); };
  const show = (id: string) => { if (!atLimit) setColumns([...visible, id]); };
  const hide = (id: string) => { if (id !== "name") setColumns(visible.filter((x) => x !== id)); };
  const move = (from: number, to: number) => {
    if (from < 1 || to < 1 || from >= visible.length || to >= visible.length) return; // Name is pinned at 0
    const next = visible.slice();
    const [row] = next.splice(from, 1);
    next.splice(to, 0, row);
    setColumns(next);
  };

  /* ── drag (handle-initiated only) ──────────────────────────────────── */
  const [dragId, setDragId] = useState<string | null>(null);
  const dragArmed = useRef(false);

  /* ── available fields ──────────────────────────────────────────────── */
  const term = search.trim().toLowerCase();
  const available = useMemo(() => {
    const out: Record<Group, CustomerListField[]> = { standard: [], sis: [], custom: [] };
    for (const f of fields ?? []) {
      if (visible.includes(f.id)) continue;
      if (f.available === false) continue;
      if (term && !(columnLabel(f.id).toLowerCase().includes(term) || f.id.toLowerCase().includes(term))) continue;
      out[f.source as Group]?.push(f);
    }
    for (const g of GROUP_ORDER) out[g].sort((a, b) => columnLabel(a.id).localeCompare(columnLabel(b.id)));
    return out;
  }, [fields, visible, term, columnLabel]);
  const availableCount = GROUP_ORDER.reduce((n, g) => n + available[g].length, 0);

  /* ── close / discard ───────────────────────────────────────────────── */
  const requestClose = useCallback(() => {
    if (saving) return;
    if (dirty) { setConfirmDiscard(true); return; }
    onClose();
  }, [dirty, saving, onClose]);

  /* ── save ──────────────────────────────────────────────────────────── */
  const validate = (cols: string[]): FieldIssue[] => {
    const found: FieldIssue[] = [];
    if (cols[0] !== "name") found.push({ id: "name", message: t("customers.columnsPanel.error.nameFirst", "Name must be the first column.") });
    if (cols.length > CUSTOMER_LIST_MAX_COLUMNS) found.push({ id: null, message: icu("customers.columnsPanel.error.tooMany", "A list can show at most {max} columns.", { max: CUSTOMER_LIST_MAX_COLUMNS }) });
    if (new Set(cols).size !== cols.length) found.push({ id: null, message: t("customers.columnsPanel.error.duplicate", "Each field can appear only once.") });
    for (const id of cols) if (!isCustomerListColumnId(id)) found.push({ id, message: icu("customers.columnsPanel.error.invalidId", "\u201C{id}\u201D isn\u2019t a valid field.", { id }) });
    for (const id of retired) found.push({ id, message: icu("customers.columnsPanel.error.retired", "\u201C{label}\u201D is no longer available. Remove it to save.", { label: columnLabel(id) }) });
    return found;
  };

  const canSave = !!base && !saving && dirty && !settingsRead.loading && !catalogRead.loading && !hasRetired;

  const save = async () => {
    if (!base || saving || !canSave) return;
    const cols = normalizeColumns(visible);
    const found = validate(cols);
    if (found.length) { setIssues(found); summaryRef.current?.focus(); return; }
    const inScope = scopeProbe();
    setSaving(true); setIssues([]); setBanner(null);
    try {
      const res = await updateCustomersSettings({
        columns: cols,
        defaultSort: base.defaultSort,
        externalIdSystem: base.externalIdSystem,
        expectedVersion: base.version,
      });
      if (!inScope()) return;
      onSaved(res.data);
      toast({ tone: "success", title: t("customers.columnsPanel.saved", "Columns saved for everyone in this organisation") });
      onClose();
    } catch (e) {
      if (!inScope()) return;
      const code = codeOf(e);
      const status = isApiError(e) ? e.status : 0;
      if (code === "CUSTOMER_LIST_SETTINGS_CONFLICT" || status === 409) {
        setBanner({
          tone: "warning", conflict: true,
          text: t("customers.columnsPanel.conflict", "Another administrator saved a different layout while this was open. Your changes are still here \u2014 reload theirs to start from it, or keep editing and save again."),
        });
      } else if (status === 422) {
        const fieldErrors = isApiError(e) ? e.fields : [];
        setIssues(fieldErrors.length
          ? fieldErrors.map((f) => ({ id: f.path ?? null, message: f.message || t("customers.columnsPanel.error.rejected", "This field was rejected.") }))
          : [{ id: null, message: t("customers.columnsPanel.error.rejected", "This field was rejected.") }]);
        summaryRef.current?.focus();
      } else if (isDenied(e)) {
        setBanner({ tone: "error", text: t("customers.columnsPanel.forbidden", "Your permission to change the list layout was removed. Nothing was saved.") });
      } else {
        setBanner({ tone: "error", text: t("customers.columnsPanel.saveFailed", "The layout couldn\u2019t be saved. Your changes are still here \u2014 try again.") });
      }
    } finally { if (inScope()) setSaving(false); }
  };

  const reloadSaved = () => { setDraft(null); setBanner(null); setIssues([]); settingsRead.reload(); };

  /* ── render ────────────────────────────────────────────────────────── */
  const loading = settingsRead.loading || catalogRead.loading;

  return (
    <Drawer
      open
      onClose={requestClose}
      width={phone ? "100%" : 480}
      title={t("customers.columnsPanel.title", "Columns")}
      description={icu(
        "customers.columnsPanel.description",
        "Choose the fields shown in the {nounPlural} list. Changes apply to everyone in this organisation.",
        { nounPlural: nounPlural.toLowerCase() },
      )}
      closeOnBackdrop
      closeOnEscape
      footer={
        confirmDiscard ? (
          <div style={{ display: "flex", gap: 8, alignItems: "center", width: "100%", flexWrap: "wrap" }}>
            <span style={{ flex: 1, minWidth: 180, fontSize: 12.5, color: T.n1 }}>
              {t("customers.columnsPanel.discardPrompt", "Discard your unsaved changes?")}
            </span>
            <Btn variant="secondary" size="sm" onClick={() => setConfirmDiscard(false)}>{t("customers.columnsPanel.keepEditing", "Keep editing")}</Btn>
            <Btn variant="danger" size="sm" onClick={onClose}>{t("customers.columnsPanel.discard", "Discard")}</Btn>
          </div>
        ) : (
          <div style={{ display: "flex", gap: 8, alignItems: "center", width: "100%", flexWrap: "wrap" }}>
            <Btn
              variant="ghost"
              size="sm"
              disabled={saving || !base}
              onClick={() => setColumns(PLATFORM_DEFAULT_COLUMNS)}
            >{t("customers.columnsPanel.reset", "Reset to default")}</Btn>
            <span style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
              <Btn variant="secondary" size="sm" disabled={saving} onClick={requestClose}>{t("common.actions.cancel", "Cancel")}</Btn>
              <Btn size="sm" disabled={!canSave} onClick={() => { void save(); }}>
                {saving ? t("common.actions.saving", "Saving\u2026") : t("common.actions.save", "Save")}
              </Btn>
            </span>
          </div>
        )
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 16, minWidth: 0 }}>
        {issues.length ? (
          <div ref={summaryRef} tabIndex={-1} role="alert" style={{ outline: "none" }}>
            <Banner tone="danger" title={t("customers.columnsPanel.error.summary", "This layout can\u2019t be saved yet")}>
              <ul style={{ margin: "6px 0 0", paddingLeft: 18, fontSize: 12.5 }}>
                {issues.map((issue, i) => (
                  <li key={`${issue.id ?? "form"}-${i}`}>
                    {issue.id ? (
                      <button
                        type="button"
                        onClick={() => rowRefs.current[issue.id!]?.scrollTo?.({ top: 0 })}
                        style={{ border: 0, background: "transparent", padding: 0, font: "inherit", color: T.n1, textDecoration: "underline", cursor: "pointer" }}
                      >{issue.message}</button>
                    ) : issue.message}
                  </li>
                ))}
              </ul>
            </Banner>
          </div>
        ) : null}

        {banner ? (
          <Banner tone={banner.tone === "error" ? "danger" : "warning"}>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <span style={{ fontSize: 12.5 }}>{banner.text}</span>
              {banner.conflict ? (
                <span style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <Btn variant="secondary" size="sm" onClick={reloadSaved}>{t("customers.columnsPanel.reloadSaved", "Reload saved settings")}</Btn>
                  <Btn variant="ghost" size="sm" onClick={() => setBanner(null)}>{t("customers.columnsPanel.keepEditing", "Keep editing")}</Btn>
                </span>
              ) : null}
            </div>
          </Banner>
        ) : null}

        {settingsRead.failure ? (
          <Banner tone="danger" title={t("customers.columnsPanel.settingsFailed", "The saved layout couldn\u2019t be read")}>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <span style={{ fontSize: 12.5, flex: 1 }}>{settingsRead.failure.message}</span>
              <Btn variant="secondary" size="sm" onClick={settingsRead.reload}>{t("common.actions.retry", "Retry")}</Btn>
            </div>
          </Banner>
        ) : null}

        {/* ── Visible columns ─────────────────────────────────────────── */}
        <section style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
            <h3 style={{ margin: 0, fontSize: 12.5, fontWeight: 700, color: T.n1 }}>{t("customers.columnsPanel.visible", "Visible columns")}</h3>
            <span style={{ marginLeft: "auto", fontSize: 11.5, color: T.soft, fontVariantNumeric: "tabular-nums" }}>
              {icu("customers.columnsPanel.selectedCount", "{count} of {max} selected", { count: visible.length, max: CUSTOMER_LIST_MAX_COLUMNS })}
            </span>
          </div>

          <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 6 }}>
            {visible.map((id, index) => {
              const isName = id === "name";
              const isRetired = catalogLoaded && !isName && !byId.has(id);
              return (
                <li
                  key={id}
                  ref={(el) => { rowRefs.current[id] = el; }}
                  draggable={dragId === id}
                  onDragStart={(e) => { if (!dragArmed.current) { e.preventDefault(); return; } e.dataTransfer.effectAllowed = "move"; }}
                  onDragEnd={() => { setDragId(null); dragArmed.current = false; }}
                  onDragOver={(e) => { if (dragId && dragId !== id && !isName) { e.preventDefault(); e.dataTransfer.dropEffect = "move"; } }}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (!dragId || dragId === id) return;
                    move(visible.indexOf(dragId), index);
                    setDragId(null); dragArmed.current = false;
                  }}
                  style={{
                    display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", minHeight: 44,
                    background: dragId === id ? T.n6 : T.white, border: `1px solid ${isRetired ? T.orange1 : T.border}`,
                    borderRadius: 6, minWidth: 0,
                  }}
                >
                  <span
                    aria-hidden={isName}
                    title={isName ? undefined : t("customers.columnsPanel.dragHandle", "Drag to reorder")}
                    onMouseDown={() => { if (!isName) { dragArmed.current = true; setDragId(id); } }}
                    onMouseUp={() => { dragArmed.current = false; setDragId(null); }}
                    style={{
                      display: "inline-flex", color: isName ? T.n4 : T.soft, cursor: isName ? "default" : "grab",
                      padding: 4, flexShrink: 0, touchAction: "none",
                    }}
                  ><Icon.drag size={14} /></span>

                  <span style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0, flex: 1 }}>
                    <span style={{ display: "flex", gap: 6, alignItems: "center", minWidth: 0 }}>
                      <span style={{ fontSize: 13, color: T.n1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{columnLabel(id)}</span>
                      <SourceBadge id={id} />
                    </span>
                    {isName ? (
                      <span style={{ fontSize: 11, color: T.soft }}>{t("customers.columnsPanel.nameLocked", "Always first, always shown.")}</span>
                    ) : isRetired ? (
                      <span style={{ fontSize: 11, color: T.orange1 }}>{t("customers.columnsPanel.retired", "No longer available \u2014 remove it to save.")}</span>
                    ) : null}
                  </span>

                  {isName ? null : (
                    <span style={{ display: "flex", gap: 2, flexShrink: 0 }}>
                      <MoveButton
                        label={icu("customers.columnsPanel.moveUp", "Move {label} up", { label: columnLabel(id) })}
                        disabled={index <= 1}
                        onClick={() => move(index, index - 1)}
                        glyph="up"
                      />
                      <MoveButton
                        label={icu("customers.columnsPanel.moveDown", "Move {label} down", { label: columnLabel(id) })}
                        disabled={index >= visible.length - 1}
                        onClick={() => move(index, index + 1)}
                        glyph="down"
                      />
                      <Btn variant="ghost" size="sm" onClick={() => hide(id)}
                        aria-label={icu("customers.columnsPanel.hideField", "Hide {label}", { label: columnLabel(id) })}>
                        {t("customers.columnsPanel.hide", "Hide")}
                      </Btn>
                    </span>
                  )}
                </li>
              );
            })}
          </ul>

          {atLimit ? (
            <p style={{ margin: 0, fontSize: 11.5, color: T.soft }}>
              {icu("customers.columnsPanel.limitReached", "This list is at its maximum of {max} columns. Hide one to add another.", { max: CUSTOMER_LIST_MAX_COLUMNS })}
            </p>
          ) : null}
        </section>

        {/* ── Available fields ────────────────────────────────────────── */}
        <section style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <h3 style={{ margin: 0, fontSize: 12.5, fontWeight: 700, color: T.n1 }}>{t("customers.columnsPanel.available", "Available fields")}</h3>
          <Input
            size="sm"
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            iconLeft={<Icon.search size={13} />}
            placeholder={t("customers.columnsPanel.searchPlaceholder", "Search fields")}
            aria-label={t("customers.columnsPanel.searchAriaLabel", "Search available fields")}
          />

          {catalogRead.failure ? (
            <Banner tone="danger" title={t("customers.columnsPanel.catalogFailed", "The field list couldn\u2019t be read")}>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <span style={{ fontSize: 12.5, flex: 1 }}>
                  {t("customers.columnsPanel.catalogFailedBody", "Fields already in this layout are still shown above and can be reordered or hidden.")}
                </span>
                <Btn variant="secondary" size="sm" onClick={catalogRead.reload}>{t("common.actions.retry", "Retry")}</Btn>
              </div>
            </Banner>
          ) : loading ? (
            <p style={{ margin: 0, fontSize: 12.5, color: T.soft }}>{t("customers.columnsPanel.loading", "Loading fields\u2026")}</p>
          ) : availableCount === 0 ? (
            <p style={{ margin: 0, fontSize: 12.5, color: T.soft }}>
              {term
                ? icu("customers.columnsPanel.noMatches", "No fields match \u201C{term}\u201D.", { term: search.trim() })
                : t("customers.columnsPanel.allShown", "Every available field is already in the list.")}
            </p>
          ) : (
            GROUP_ORDER.filter((g) => available[g].length).map((g) => (
              <div key={g} style={{ border: `1px solid ${T.border}`, borderRadius: 6, background: T.white, overflow: "hidden" }}>
                <button
                  type="button"
                  aria-expanded={!collapsed[g]}
                  onClick={() => setCollapsed((c) => ({ ...c, [g]: !c[g] }))}
                  style={{
                    display: "flex", alignItems: "center", gap: 8, width: "100%", minHeight: 44, padding: "10px 12px",
                    border: 0, background: "transparent", cursor: "pointer", fontFamily: T.font, textAlign: "left",
                  }}
                >
                  <span style={{ display: "inline-flex", color: T.soft }}><Icon.chev dir={collapsed[g] ? "right" : "down"} size={13} /></span>
                  <span style={{ fontSize: 12.5, fontWeight: 700, color: T.n1 }}>
                    {g === "standard" ? t("customers.columnsPanel.groupStandard", "Standard fields")
                      : g === "sis" ? t("customers.columnsPanel.groupSis", "SIS attributes")
                        : t("customers.columnsPanel.groupCustom", "Custom attributes")}
                  </span>
                  <span style={{ marginLeft: "auto", fontSize: 11.5, color: T.soft, fontVariantNumeric: "tabular-nums" }}>{available[g].length}</span>
                </button>
                {collapsed[g] ? null : (
                  <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                    {available[g].map((f) => (
                      <li key={f.id} style={{
                        display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", minHeight: 44,
                        borderTop: `1px solid ${T.n6}`, minWidth: 0,
                      }}>
                        <span style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0, flex: 1 }}>
                          <span style={{ display: "flex", gap: 6, alignItems: "center", minWidth: 0 }}>
                            <span style={{ fontSize: 13, color: T.n1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{columnLabel(f.id)}</span>
                            <SourceBadge id={f.id} />
                          </span>
                          <span style={{ fontFamily: T.fontMono, fontSize: 10.5, color: T.soft }}>{f.id}</span>
                        </span>
                        <Btn
                          variant="secondary"
                          size="sm"
                          disabled={atLimit}
                          onClick={() => show(f.id)}
                          aria-label={icu("customers.columnsPanel.showField", "Show {label}", { label: columnLabel(f.id) })}
                        >{t("customers.columnsPanel.show", "Show")}</Btn>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))
          )}
        </section>
      </div>
    </Drawer>
  );
}

function MoveButton({ label, disabled, onClick, glyph }: { label: string; disabled: boolean; onClick: () => void; glyph: "up" | "down" }) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      style={{
        width: 28, height: 28, display: "grid", placeItems: "center", borderRadius: 6,
        border: `1px solid ${T.border}`, background: T.white, color: disabled ? T.n4 : T.n2,
        cursor: disabled ? "default" : "pointer", flexShrink: 0,
      }}
    ><Icon.chev dir={glyph} size={12} /></button>
  );
}
